import is from '@sindresorhus/is';
import { motion } from 'framer-motion';
import { type CSSProperties, type FunctionComponent, useEffect, useMemo, useState } from 'react';

import { useChatThreadMessages } from './api';
import { findInputOverrideForMessage, type InputOverrideRenderResult } from './block-renderers';
import {
    useChatComposerActive,
    useChatComposerCallbacks,
    useChatComposerConversation,
    useChatComposerInput,
} from './components/ChatComposerContext';
import { ChatHeader } from './components/ChatHeader';
import { ChatMessageList } from './components/ChatMessageList';
import { ChatPromptOutlet } from './components/ChatPromptOutlet';
import { ChatSkillSidepanelHost } from './components/ChatSkillSidepanel';
import { PillChatComposer } from './components/PillChatComposer';

// Sequenced mount: card lands first, then the composer drops in, then the
// contextual prompt lands last. Reads top-down: "card opens → composer
// follows → context settles".
const CARD_INITIAL = { opacity: 0, y: 24 } as const;
const CARD_ANIMATE = { opacity: 1, y: 0 } as const;
const CARD_TRANSITION = {
    opacity: { delay: 0.12, duration: 0.25, ease: 'easeOut' as const },
    y: { delay: 0.12, duration: 0.3, ease: 'easeOut' as const },
} as const;

const PILL_INITIAL = { opacity: 0, y: 16 } as const;
const PILL_ANIMATE = { opacity: 1, y: 0 } as const;
const PILL_TRANSITION = {
    opacity: { delay: 0.26, duration: 0.26, ease: 'easeOut' as const },
    y: { delay: 0.26, duration: 0.32, ease: 'easeOut' as const },
} as const;

const PROMPT_MOUNT_DELAY_MS = 800;
const PROMPT_INITIAL = { opacity: 0, y: 24 } as const;
const PROMPT_ANIMATE = { opacity: 1, y: 0 } as const;
const PROMPT_TRANSITION = {
    opacity: { duration: 0.26, ease: 'easeOut' as const },
    y: { duration: 0.32, ease: 'easeOut' as const },
} as const;

// Lift treatment driven by `isChatScrolled` from context. CSS transitions
// (not framer-motion) so they don't re-render the subtree on every scroll
// frame.
const LIFT_GUTTER_PX = 6;
const LIFT_SHADOW = '0 10px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)';
const LIFT_OUTER_TRANSITION = 'padding-bottom 200ms ease-out, margin-top 200ms ease-out';
const LIFT_SHADOW_TRANSITION = 'box-shadow 360ms cubic-bezier(0.25, 1, 0.5, 1)';

const buildOuterStyle = (isChatScrolled: boolean): CSSProperties => ({
    paddingBottom: isChatScrolled ? LIFT_GUTTER_PX : 0,
    marginTop: isChatScrolled ? -LIFT_GUTTER_PX : 0,
    marginBottom: isChatScrolled ? -LIFT_GUTTER_PX : 0,
    flex: 1,
    transition: LIFT_OUTER_TRANSITION,
});

const buildCardShadowStyle = (isChatScrolled: boolean): CSSProperties => ({
    boxShadow: isChatScrolled ? LIFT_SHADOW : 'none',
    transition: LIFT_SHADOW_TRANSITION,
});

export const ChatBody: FunctionComponent = () => {
    const { close, handleDraftChange, handleKeyDown, submit } = useChatComposerCallbacks();
    const { activeChatContext, isChatScrolled } = useChatComposerActive();
    const { draft, transcription } = useChatComposerInput();
    const conversation = useChatComposerConversation();

    const messagesQuery = useChatThreadMessages(conversation.threadId ?? undefined, {
        enabled: !conversation.isFreshThread,
    });

    const isStreaming = conversation.turn.status === 'streaming';

    const latestAssistant = useMemo(() => {
        const messages = messagesQuery.data?.pages.flatMap((page) => page.messages) ?? [];
        return messages.find((message) => message.role === 'assistant');
    }, [messagesQuery.data]);

    const inputOverride = useMemo<InputOverrideRenderResult | null>(() => {
        if (is.undefined(latestAssistant)) return null;
        return findInputOverrideForMessage(latestAssistant, conversation.awaitingCallback);
    }, [latestAssistant, conversation.awaitingCallback]);
    const hasPrompt = activeChatContext !== null && !isStreaming;
    const [showPrompt, setShowPrompt] = useState(false);
    useEffect(() => {
        if (!hasPrompt) {
            setShowPrompt(false);
            return;
        }
        const timeout = setTimeout(() => setShowPrompt(true), PROMPT_MOUNT_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [hasPrompt]);
    return (
        <ChatSkillSidepanelHost>
            <div className="z-999 flex h-full min-h-0 flex-col" style={buildOuterStyle(isChatScrolled)}>
                <motion.div
                    className="flex h-full min-h-0 flex-col overflow-hidden rounded-xxxl border border-gray-300 bg-white-200"
                    initial={CARD_INITIAL}
                    animate={CARD_ANIMATE}
                    transition={CARD_TRANSITION}
                    style={buildCardShadowStyle(isChatScrolled)}
                >
                    <div className="px-xxl shrink-0">
                        <ChatHeader onClose={close} />
                    </div>
                    <div className="ws-scroll min-h-0 flex flex-1 pb-5">
                        <ChatMessageList
                            awaitingCallback={conversation.awaitingCallback}
                            isLoading={messagesQuery.isLoading}
                            pages={messagesQuery.data?.pages ?? []}
                        />
                    </div>
                    {showPrompt && (
                        <motion.div
                            className="shrink-0 px-m pb-m"
                            initial={PROMPT_INITIAL}
                            animate={PROMPT_ANIMATE}
                            transition={PROMPT_TRANSITION}
                        >
                            <div className="rounded-lg shadow-sm empty:hidden">
                                <ChatPromptOutlet />
                            </div>
                        </motion.div>
                    )}
                    {inputOverride?.suppressInputChrome !== true ? (
                        <motion.div
                            className="shrink-0 px-m pb-m"
                            initial={PILL_INITIAL}
                            animate={PILL_ANIMATE}
                            transition={PILL_TRANSITION}
                        >
                            <PillChatComposer
                                draft={draft}
                                inputOverride={inputOverride?.node}
                                isExpanded={true}
                                isStreaming={isStreaming}
                                transcription={transcription}
                                onDraftChange={handleDraftChange}
                                onKeyDown={handleKeyDown}
                                onSubmit={submit}
                            />
                        </motion.div>
                    ) : null}
                </motion.div>
            </div>
        </ChatSkillSidepanelHost>
    );
};
